import { motion } from "framer-motion";
import { ChevronRight, Play, FileText, Factory, Lock, Route, Phone, LogOut } from "lucide-react";
import { DynIcon } from "@/lib/icons";
import { mediaRaw } from "@/lib/api";
import { logEvent } from "@/lib/kioskData";
import { ImzLogo, BsnlLogo } from "@/components/Brand";
import { AutoFit } from "../AutoFit";
import { KioskButton, SectionLabel, TopBar, ScreenShell, Pill, useKiosk } from "../ui";

function resolveImg(url) {
  return url || "";
}

const PARTICLES = [
  { left: "12%", top: "22%", size: 6, dur: 7, delay: 0 },
  { left: "82%", top: "30%", size: 4, dur: 9, delay: 1.2 },
  { left: "26%", top: "72%", size: 5, dur: 8, delay: 0.6 },
  { left: "68%", top: "66%", size: 7, dur: 10, delay: 1.8 },
  { left: "48%", top: "18%", size: 3, dur: 6, delay: 0.3 },
  { left: "90%", top: "58%", size: 5, dur: 11, delay: 2.1 },
  { left: "8%", top: "52%", size: 4, dur: 9, delay: 1.5 },
  { left: "56%", top: "82%", size: 6, dur: 8, delay: 0.9 },
];

// Ambient animated background for the attract/welcome screen.
function WelcomeAura() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden data-testid="welcome-aura">
      <motion.div
        className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-cyan-500/20 blur-3xl"
        animate={{ x: [0, 50, 0], y: [0, 40, 0], scale: [1, 1.18, 1] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-28 -right-20 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl"
        animate={{ x: [0, -60, 0], y: [0, -35, 0], scale: [1, 1.22, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-400/10"
        animate={{ scale: [1, 1.12, 1], opacity: [0.35, 0.6, 0.35] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      {PARTICLES.map((p, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-cyan-300/70 shadow-[0_0_12px_rgba(34,211,238,0.9)]"
          style={{ left: p.left, top: p.top, width: p.size, height: p.size }}
          animate={{ y: [0, -22, 0], opacity: [0.2, 0.9, 0.2] }}
          transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: p.delay }}
        />
      ))}
    </div>
  );
}

export function WelcomeScreen() {
  const { branding, nav, startExperience } = useKiosk();
  const b = branding || {};
  const words = b.tagline_words || ["Secure", "Analyze", "Optimize"];
  const go = () => { startExperience(); nav("solutions"); };

  return (
    <motion.div
      data-testid="kiosk-welcome-screen"
      onClick={go}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
      className="relative flex h-full flex-col items-center justify-between overflow-hidden px-6 py-8 text-center cursor-pointer kiosk-radial"
    >
      <WelcomeAura />
      <div className="tricolor-strip absolute inset-x-0 top-0 h-1 opacity-70" />
      <div className="z-10 flex w-full items-center justify-between">
        <BsnlLogo />
        <ImzLogo />
      </div>

      <div className="z-10 flex flex-1 flex-col items-center justify-center gap-6 py-6">
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="font-head text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] text-white"
        >
          {b.headline_line1 || "Securing"}
          <br />
          {b.headline_line2 || "India's"} <span className="cyan-text">Assets</span>
        </motion.h1>

        <div className="flex items-center gap-3 font-head text-base sm:text-xl font-semibold text-slate-200">
          {words.map((w, i) => (
            <span key={w} className="flex items-center gap-3">
              {i > 0 && <span className="text-cyan-400/60">|</span>}
              <span>{w}</span>
            </span>
          ))}
        </div>
        <p className="max-w-md text-sm sm:text-base text-slate-400">{b.subtitle}</p>
      </div>

      <div className="z-10 mb-10 sm:mb-16 flex flex-col items-center gap-3">
        <div className="relative">
          <span className="pulse-ring absolute inset-0 rounded-full border border-cyan-400/60" />
          <KioskButton testId="kiosk-welcome-tap-cta" className="px-10 py-5 text-lg">
            {b.cta_label || "Tap to Explore"} <ChevronRight className="h-5 w-5" />
          </KioskButton>
        </div>
        <span className="font-tele text-[10px] uppercase tracking-[0.3em] text-slate-500">{b.footer_text}</span>
      </div>

      <a
        href="/admin/login"
        onClick={(e) => e.stopPropagation()}
        data-testid="kiosk-admin-access"
        className="absolute bottom-3 left-3 z-20 flex h-9 w-9 items-center justify-center rounded-lg text-slate-700 hover:text-cyan-400/60 transition-colors"
        aria-label="Admin"
      >
        <Lock className="h-4 w-4" />
      </a>
    </motion.div>
  );
}

export function SolutionsScreen() {
  const { solutions, nav, branding } = useKiosk();
  const list = (solutions || []).filter((s) => s.status === "published");

  return (
    <ScreenShell>
      <TopBar onBack={null} />
      <div className="min-h-0 flex-1 overflow-hidden px-5 sm:px-8 py-5" data-testid="kiosk-solutions-grid">
        <AutoFit>
        <div className="mb-6">
          <SectionLabel>Explore</SectionLabel>
          <h1 className="font-head text-3xl sm:text-5xl font-extrabold text-white mt-2">Our Solutions</h1>
          <p className="mt-2 text-sm sm:text-base text-slate-400">{branding?.intro_text || "Tap on any solution to know more."}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((s, i) => (
            <motion.button
              key={s.id}
              data-testid={`kiosk-solution-card-${i}`}
              onClick={() => {
                logEvent("solution_opened", s.name, s.id);
                if (s.name === "Journey Visibility") { logEvent("journey_opened", s.name, s.id); nav("journey"); }
                else nav("solution", { solution: s });
              }}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="group relative overflow-hidden rounded-3xl glass glow-border-hover p-6 text-left min-h-[180px] flex flex-col"
            >
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl cyan-grad text-[#041021]">
                <DynIcon name={s.icon} className="h-7 w-7" />
              </div>
              <h3 className="font-head text-xl font-bold text-white">{s.name}</h3>
              <p className="mt-1.5 text-sm text-slate-400 line-clamp-2">{s.short_description}</p>
              <ChevronRight className="absolute bottom-5 right-5 h-5 w-5 text-cyan-400/70 transition-transform group-active:translate-x-1" />
            </motion.button>
          ))}
        </div>

        <motion.button
          data-testid="kiosk-industry-entry"
          onClick={() => nav("industries")}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="mt-5 flex w-full items-center justify-between rounded-3xl p-6 glass glow-border-hover"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-400/40 text-cyan-300">
              <Factory className="h-7 w-7" />
            </div>
            <div className="text-left">
              <h3 className="font-head text-xl font-bold text-white">Industry Applications</h3>
              <p className="text-sm text-slate-400">Explore how our solutions power different sectors.</p>
            </div>
          </div>
          <ChevronRight className="h-6 w-6 text-cyan-400/70" />
        </motion.button>

        <div className="mt-5 grid grid-cols-3 gap-3">
          <button data-testid="kiosk-nav-journey" onClick={() => { logEvent("journey_opened"); nav("journey"); }}
            className="flex flex-col items-center gap-2 rounded-2xl glass glow-border-hover py-4 text-cyan-100">
            <Route className="h-6 w-6" /><span className="text-xs font-semibold">Journey</span>
          </button>
          <button data-testid="kiosk-nav-contact" onClick={() => { logEvent("contact_opened"); nav("contact"); }}
            className="flex flex-col items-center gap-2 rounded-2xl glass glow-border-hover py-4 text-cyan-100">
            <Phone className="h-6 w-6" /><span className="text-xs font-semibold">Contact</span>
          </button>
          <button data-testid="kiosk-nav-finish" onClick={() => nav("thankyou")}
            className="flex flex-col items-center gap-2 rounded-2xl glass glow-border-hover py-4 text-cyan-100">
            <LogOut className="h-6 w-6" /><span className="text-xs font-semibold">Finish</span>
          </button>
        </div>
        </AutoFit>
      </div>
    </ScreenShell>
  );
}

export function SolutionDetail({ solution }) {
  const { nav, back } = useKiosk();
  const s = solution;
  const img = resolveImg(s.hero_image_url || s.thumbnail_url);
  const hasVideo = (s.videos || []).length > 0;
  const hasPdf = (s.pdfs || []).length > 0;

  return (
    <ScreenShell>
      <TopBar title={s.name} onBack={back} />
      <div className="min-h-0 flex-1 overflow-hidden px-5 sm:px-8 py-5" data-testid="kiosk-solution-detail-view">
        <AutoFit>
        <div className="grid grid-cols-1 content-start gap-x-8 gap-y-4 landscape:grid-cols-2 landscape:items-start">
          <p className="landscape:col-span-2 text-base text-cyan-100/90">{s.subtitle}</p>

          {img && (
            <div className="relative overflow-hidden rounded-3xl glow-border landscape:col-start-1 landscape:row-start-2">
              <img src={img} alt={s.name} className="h-44 landscape:h-72 w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050b18]/80 to-transparent" />
            </div>
          )}

          <div className="landscape:col-start-2 landscape:row-start-2">
            <p className="text-base leading-relaxed text-slate-300">{s.description}</p>

            {(s.benefits || []).length > 0 && (
              <div className="mt-4">
                <SectionLabel>Key Benefits</SectionLabel>
                <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {s.benefits.map((b) => (
                    <div key={b} className="flex items-center gap-3 rounded-xl glass px-3.5 py-2.5">
                      <span className="h-2 w-2 shrink-0 rounded-full cyan-grad" />
                      <span className="text-sm text-slate-200">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(s.capabilities || []).length > 0 && (
              <div className="mt-4">
                <SectionLabel>Capabilities</SectionLabel>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {s.capabilities.map((c) => <Pill key={c} tone="blue">{c}</Pill>)}
                </div>
              </div>
            )}

            {(s.asset_types || []).length > 0 && (
              <div className="mt-4">
                <SectionLabel>Built For</SectionLabel>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {s.asset_types.map((a) => <Pill key={a}>{a}</Pill>)}
                </div>
              </div>
            )}
          </div>
        </div>
        </AutoFit>
      </div>

      <div className="shrink-0 border-t border-cyan-400/15 glass px-5 sm:px-8 py-4">
        {(hasVideo || hasPdf) ? (
          <div className="flex flex-col sm:flex-row gap-3">
            {hasVideo && (
              <KioskButton testId="kiosk-watch-video-cta" className="flex-1" onClick={() => { const v = s.videos[0]; const srcs = []; if (v.url) srcs.push({ src: v.url, type: "video/mp4" }); if (v.url_webm) srcs.push({ src: v.url_webm, type: "video/webm" }); logEvent("video_played", s.name, s.id); nav("video", { title: v.title || `${s.name} Video`, url: v.url, sources: srcs, description: v.description }); }}>
                <Play className="h-5 w-5" /> Watch Video
              </KioskButton>
            )}
            {hasPdf && (
              <KioskButton testId="kiosk-view-pdf-cta" variant="outline" className="flex-1" onClick={() => { const p = s.pdfs[0]; logEvent("pdf_opened", p.title || s.name, s.id); nav("pdf", { title: p.title || `${s.name} Document`, mediaId: p.media_id, url: p.url, emailEnabled: s.email_enabled, solutionId: s.id }); }}>
                <FileText className="h-5 w-5" /> View PDF Details
              </KioskButton>
            )}
          </div>
        ) : (
          <p className="text-center text-sm text-slate-500">Media for this solution will appear here once published by the team.</p>
        )}
      </div>
    </ScreenShell>
  );
}
